import { Spinner } from '@/components/glitch-ui/spinner';
import { createFileRoute, Link, redirect } from '@tanstack/react-router';
import { Users, Swords, Linkedin, Facebook, Instagram, Eye, FilePlus, FolderPlus, AlertCircle, Trash2, Plus } from 'lucide-react';
import { EmptyState, PageLoadingState } from '@/components/glitch-ui/empty-state';
import { getCampaignShortName } from '@/lib/utils';
import { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useTableSorting } from '@/hooks/useTableSorting';
import { SortableHeader } from '@/components/glitch-ui/data-table/sortable-header';
import { DataTable } from '@/components/glitch-ui/data-table';
import type { ColumnDef } from '@tanstack/react-table';
import type { CampaignData } from '@/lib/types';
import { CAMPAIGN_STATE } from '@/lib/types';
import { useUnsavedChangesWarning } from '@/hooks/useUnsavedChangesWarning';
import { usePostHog } from 'posthog-js/react';
import { Button } from '@/components/ui/button';
import { SaveChangesButton } from '@/components/glitch-ui/save-changes-button';
import { CampaignPill, CAMPAIGN_PILL_SHAPE, PILL_METRICS } from '@/components/glitch-ui/campaign-pill';

import { generateCompetitorReport, deleteCompetitor, startCompetitorGeneration, updateCompetitor, addCompetitorsToCampaign } from '@/lib/apis';
import { useAtomValue } from 'jotai';
import { organizationDataAtom, googleAdsAccountAtom } from '@/store';
import { competitorProximityLabel } from '@/lib/competitor-proximity';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { AddCompetitorModal } from '@/components/competitors/add-competitor-modal';
import { ConformationModal } from '@/components/glitch-ui/confirmation-modal';
import { competitorTasksQueryOptions, useReportsQuery, reportTasksQueryOptions, businessQueryOptions, competitorsByBusinessQueryOptions, campaignsQueryOptions } from '@/lib/query-options';
import { usePaymentGate } from '@/hooks/usePaymentGate';
import { Select, SelectContent, SelectItem, SelectTrigger } from '@/components/ui/select';
import { useToast } from '@/hooks/useToast';

export const Route = createFileRoute('/_authenticated/_layout/competitors/')({
  // Competitors moved into the Business page (#competitors tab); keep old URLs working.
  beforeLoad: () => {
    throw redirect({ to: '/business', hash: 'competitors' });
  },
});

// The colours are this table's; the word itself comes from the one band map
// (#6717), so the export and the table call a competitor the same thing.
const PROXIMITY_COLORS: Record<string, { bgColor: string; textColor: string }> = {
  'Sub-brand': { bgColor: 'bg-green-100', textColor: 'text-green-600' },
  Direct: { bgColor: 'bg-orange-50', textColor: 'text-orange-600' },
  Adjacent: { bgColor: 'bg-purple-50', textColor: 'text-purple-600' },
  Complementary: { bgColor: 'bg-indigo-50', textColor: 'text-indigo-600' },
  Unrelated: { bgColor: 'bg-gray-100', textColor: 'text-gray-600' },
};

function getCompetitorRelationship(closeness: number): { label: string; bgColor: string; textColor: string } {
  const label = competitorProximityLabel(closeness);
  return { label, ...PROXIMITY_COLORS[label] };
}

// #4792: the 5 editable Proximity relationships and their representative closeness scores. The
// auto-assigned closeness is a 0–1 float; editing snaps it to one of these representative values.
const PROXIMITY_RELATIONSHIPS = [
  { label: 'Sub-brand', score: 1.0 },
  { label: 'Direct', score: 0.8 },
  { label: 'Adjacent', score: 0.5 },
  { label: 'Complementary', score: 0.2 },
  { label: 'Unrelated', score: 0.0 },
] as const;
const PROXIMITY_SCORE_BY_LABEL: Record<string, number> = Object.fromEntries(
  PROXIMITY_RELATIONSHIPS.map((r) => [r.label, r.score]),
);

// Putting a competitor in a campaign is one choice, so it is one dropdown on
// the row (#6743), the same idiom as Proximity one column over — not a trip to
// a full-screen wizard to answer a single question. The list offers the
// competitor campaigns that exist and nothing else: a campaign is created on
// the campaign screens, where its goal, budget and ads are decided, never as a
// side effect of filing a competitor. With no campaign to file into, the
// control is disabled and says why.
const NO_CAMPAIGN_REASON =
  'There is no competitor campaign to add this to. Create one from Campaigns first.';

// A domain the research found nothing behind. Targeting it would spend the budget on a
// competitor that does not exist, so the control is disabled and says so (#6754).
const UNRESOLVED_SITE_REASON = 'There is no company behind this site, so there is nothing to target.';

function AddToCampaignSelect({
  campaigns,
  loading,
  busy,
  unavailableReason,
  onPick,
}: {
  campaigns: CampaignData[];
  loading: boolean;
  busy: boolean;
  unavailableReason?: string;
  onPick: (campaignId: number) => void;
}) {
  const disabled = loading || busy || campaigns.length === 0 || !!unavailableReason;
  const reason = unavailableReason
    ? unavailableReason
    : busy
      ? 'Adding to campaign'
      : loading
        ? 'Loading campaigns'
        : campaigns.length === 0
          ? NO_CAMPAIGN_REASON
          : undefined;
  return (
    // The title sits on the wrapper, not the trigger: a disabled trigger takes
    // no pointer events, so a tooltip on it would never show.
    <span title={reason} onClick={(e) => e.stopPropagation()} className="inline-flex whitespace-nowrap">
      <Select value="" onValueChange={(value) => onPick(Number(value))} disabled={disabled}>
        <SelectTrigger
          onClick={(e) => e.stopPropagation()}
          // The base trigger lays its child span out as a block, which stacked
          // the icon above the word; the pill needs it on one line.
          className={`h-auto w-fit border-0 ${PILL_METRICS} focus:ring-1 focus:ring-[#290BD9] [&>span]:!inline-flex [&>span]:items-center [&>span]:gap-1 [&>svg]:static [&>svg]:ml-1 [&>svg]:size-3 [&>svg]:opacity-60 ${
            disabled ? 'bg-gray-50 text-gray-400' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          <span>
            {busy ? <Spinner size="sm" className="text-gray-400" /> : <FolderPlus className="h-3 w-3 shrink-0" />}
            Add
          </span>
        </SelectTrigger>
        <SelectContent>
          {campaigns.map((campaign) => (
            <SelectItem key={campaign.id} value={String(campaign.id)} className="text-sm">
              {getCampaignShortName(campaign)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </span>
  );
}

// The Campaign column is one pill wide, whatever it holds (#6743). The pill
// itself is the house one, so a campaign looks the same here as on a location
// row. A campaign still being built has a placeholder name that means nothing
// to the reader, so it wears the same shape in grey and says Generating.
function CompetitorCampaignCell({ campaign, generating }: { campaign: CompetitorCampaignInfo; generating: boolean }) {
  if (generating) {
    return (
      <span
        title="Glitch is still building this campaign."
        className={`${CAMPAIGN_PILL_SHAPE} bg-gray-100 text-gray-500`}
      >
        <Spinner size="sm" className="mr-1 inline-block align-[-0.15em] text-gray-400" />
        Generating
      </span>
    );
  }
  return (
    <CampaignPill
      slug={campaign.slug}
      name={getCampaignShortName(campaign)}
      onClick={(e) => e.stopPropagation()}
      className="max-w-[220px]"
    />
  );
}

// Editable Proximity control: a badge-styled Select of the 5 relationships, preserving the existing
// color mapping. stopPropagation so opening it doesn't also select the competitor row.
function ProximitySelect({
  closeness,
  onChange,
  disabled,
}: {
  closeness: number;
  onChange: (score: number) => void;
  disabled?: boolean;
}) {
  const relationship = getCompetitorRelationship(closeness);
  return (
    <Select
      value={relationship.label}
      onValueChange={(label) => {
        const score = PROXIMITY_SCORE_BY_LABEL[label];
        if (score !== undefined && score !== closeness) onChange(score);
      }}
      disabled={disabled}
    >
      <SelectTrigger
        onClick={(e) => e.stopPropagation()}
        className={`h-auto w-fit rounded-full border-0 px-2.5 py-1 text-xs font-medium focus:ring-1 focus:ring-[#290BD9] [&>svg]:static [&>svg]:ml-1 [&>svg]:size-3 [&>svg]:opacity-60 ${relationship.bgColor} ${relationship.textColor}`}
      >
        {relationship.label}
      </SelectTrigger>
      <SelectContent>
        {PROXIMITY_RELATIONSHIPS.map(({ label }) => {
          const r = getCompetitorRelationship(PROXIMITY_SCORE_BY_LABEL[label]);
          return (
            <SelectItem key={label} value={label} className="text-xs">
              <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${r.bgColor} ${r.textColor}`}>
                {label}
              </span>
            </SelectItem>
          );
        })}
      </SelectContent>
    </Select>
  );
}

interface CompetitorAdInfo {
  id: number;
  slug: string;
}

interface CompetitorCampaignInfo {
  id: number;
  slug: string;
  name: string;
  ad: CompetitorAdInfo | null;
}

interface Competitor {
  id: string;
  business_id: number;
  competitor_brand_name: string;
  primary_website: string | null;
  social_media: {
    linkedin?: string;
    twitter?: string;
    facebook?: string;
    instagram?: string;
    youtube?: string;
  };
  competitor_closeness: number;
  description: string | null;
  location: {
    town?: string;
    region?: string;
    country?: string;
  };
  notes: string | null;
  competitor_metadata: {
    industry?: string;
    audience?: string;
    founders?: string[];
    founding_year?: number;
    company_size?: string;
    headquarters?: string;
  };
  created_at: string;
  updated_at: string;
  is_loading: boolean;
  // False when the research found no company behind the domain: parked, dead, or
  // unidentifiable. The row stays so the reader can see what happened to a URL somebody
  // asked for, but it is not a competitor and cannot be targeted (#6754).
  site_resolved: boolean;
  campaign: CompetitorCampaignInfo | null;
}

// Rendered inside the Business page's Competitors tab (#6672): /competitors
// itself redirects there. The buttons are drawn into the Business toolbar
// slot passed as `actionsContainer`.
export function CompetitorsPage({ actionsContainer = null }: { actionsContainer?: HTMLElement | null } = {}) {
  const queryClient = useQueryClient();
  const posthog = usePostHog();
  const { toast } = useToast();
  const organizationData = useAtomValue(organizationDataAtom);
  const googleAdsAccount = useAtomValue(googleAdsAccountAtom);
  const [selectedCompetitorId, setSelectedCompetitorId] = useState<string | null>(null);

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [generatingReportIds, setGeneratingReportIds] = useState<Set<string>>(new Set());
  const [competitorToDelete, setCompetitorToDelete] = useState<Competitor | null>(null);
  // Fetch business data for the current organization (1:1 relationship)
  const { data: business, isLoading: isBusinessLoading } = useQuery({
    ...businessQueryOptions(organizationData?.id || 0),
    enabled: !!organizationData?.id,
  });

  // Competitors via the canonical query options (#4907) — the previous
  // useEffect+setState mirror bypassed cache/dedup/invalidation and drifted.
  const {
    data: serverCompetitors = [],
    isLoading,
    error: competitorsError,
  } = useQuery({
    ...competitorsByBusinessQueryOptions(business?.id ?? 0),
    select: (data): Competitor[] => data as Competitor[],
  });
  const error = competitorsError ? 'Failed to load competitors' : null;
  const isInitialLoading = isBusinessLoading || isLoading;

  // The campaigns the row's dropdown offers (#6743): competitor campaigns
  // only, since that is the only kind a competitor belongs in.
  const { data: allCampaigns = [], isLoading: isLoadingCampaignsQuery } = useQuery({
    ...campaignsQueryOptions(organizationData?.id, googleAdsAccount?.googleads_account_id),
  });
  const competitorCampaigns = useMemo(
    () => allCampaigns.filter((c) => c.goal === 'competitor'),
    [allCampaigns],
  );
  // Nothing to load is not loading: with no Google Ads account the query never
  // runs, and the reader should read the reason, not a permanent spinner.
  const isLoadingCompetitorCampaigns = isLoadingCampaignsQuery && !!googleAdsAccount?.googleads_account_id;

  // A campaign Glitch is still building carries a placeholder name that means
  // nothing to the reader, so the pill says Generating instead (#6743). The
  // competitor payload carries only id, slug and name, so the state comes from
  // the campaigns the dropdown already loaded.
  const isCampaignGenerating = (campaignId: number) =>
    allCampaigns.find((c) => c.id === campaignId)?.state === CAMPAIGN_STATE.CREATING;

  const [addingCompetitorId, setAddingCompetitorId] = useState<string | null>(null);
  const addToCampaign = async (competitor: Competitor, campaignId: number) => {
    setAddingCompetitorId(competitor.id);
    try {
      const result = await addCompetitorsToCampaign(campaignId, [competitor.id]);
      await queryClient.invalidateQueries({ queryKey: ['competitors'] });
      await queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      posthog?.capture('competitors_added_to_campaign', {
        campaign_id: campaignId,
        competitors_added: result.added.length,
        competitors_skipped: result.skipped.length,
        keywords_added: result.keywords_added,
      });
      if (result.added.length === 0) {
        toast({
          title: 'Already in the campaign',
          description: `${competitor.competitor_brand_name} is already in that campaign.`,
        });
      }
    } catch (err) {
      toast({
        title: 'Could not add to campaign',
        description: err instanceof Error ? err.message : 'The competitor could not be added.',
        variant: 'destructive',
      });
    } finally {
      setAddingCompetitorId(null);
    }
  };

  // #4792: proximity edits stage locally, keyed by competitor id, and persist only on "Save Changes".
  // The competitor list stays owned by React Query (#4907); staged edits are overlaid at render time
  // rather than mutating the cached array, so the query remains the single source of truth.
  const [stagedCloseness, setStagedCloseness] = useState<Record<string, number>>({});
  const [isSavingProximity, setIsSavingProximity] = useState(false);
  const competitors = useMemo(
    () =>
      serverCompetitors.map((c) =>
        c.id in stagedCloseness ? { ...c, competitor_closeness: stagedCloseness[c.id] } : c,
      ),
    [serverCompetitors, stagedCloseness],
  );

  // #4792: stage a competitor's Proximity (closeness) edit. Compared against the SERVER value so
  // re-selecting the original relationship clears the staged edit and the page goes clean again.
  const handleProximityChange = (competitor: Competitor, score: number) => {
    const serverValue = serverCompetitors.find((c) => c.id === competitor.id)?.competitor_closeness;
    setStagedCloseness((prev) => {
      const next = { ...prev };
      if (serverValue === undefined || score === serverValue) {
        delete next[competitor.id];
      } else {
        next[competitor.id] = score;
      }
      return next;
    });
  };

  // Dirty when any competitor has a staged proximity edit.
  const isDirty = Object.keys(stagedCloseness).length > 0;

  // #4792: persist every staged proximity change, then invalidate so the query refetches the
  // canonical values and the overlay clears.
  const saveProximityChanges = async () => {
    const changed = Object.entries(stagedCloseness);
    if (changed.length === 0) return;
    setIsSavingProximity(true);
    try {
      await Promise.all(
        changed.map(([id, closeness]) => updateCompetitor(id, { competitor_closeness: closeness })),
      );
      changed.forEach(([id, closeness]) => {
        posthog?.capture('competitor_proximity_edited', { competitor_id: id, closeness });
      });
      setStagedCloseness({});
      if (business?.id) {
        queryClient.invalidateQueries({ queryKey: ['competitors', business.id] });
      }
    } catch {
      toast({ title: 'Could not save proximity changes', description: 'Please try again.', variant: 'destructive' });
    } finally {
      setIsSavingProximity(false);
    }
  };

  // Revert every staged proximity edit back to the last-saved server values.
  const discardProximityChanges = () => setStagedCloseness({});

  const { UnsavedChangesModal } = useUnsavedChangesWarning({
    isDirty,
    onSave: saveProximityChanges,
    onDiscard: discardProximityChanges,
    isSaving: isSavingProximity,
    allowedBasePath: '/competitors',
    contextName: 'Competitors',
  });

  const hasTrackedPageView = useRef(false);

  const generateMutation = useMutation({
    mutationFn: () => startCompetitorGeneration(business!.business_url, 10, business!.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['competitor-tasks', business?.id] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (competitorId: string) => deleteCompetitor(competitorId),
    onSuccess: () => {
      posthog?.capture('competitor_deleted', { competitor_id: competitorToDelete?.id });
      queryClient.invalidateQueries({ queryKey: ['competitors', business?.id] });
      // Clear selection if deleted competitor was selected
      if (selectedCompetitorId === competitorToDelete?.id) {
        setSelectedCompetitorId(null);
      }
      setCompetitorToDelete(null);
    },
  });

  // Track competitors page viewed - only once per mount
  useEffect(() => {
    if (!hasTrackedPageView.current && !isLoading) {
      hasTrackedPageView.current = true;
      posthog?.capture('competitors_page_viewed', {
        organization_id: organizationData?.id,
        competitor_count: competitors.length,
      });
    }
  }, [isLoading, competitors.length, posthog, organizationData?.id]);

  // Update URL when competitor selection changes
  const selectCompetitor = (competitorId: string | null) => {
    setSelectedCompetitorId(competitorId);

    // Track competitor selection
    if (competitorId) {
      const competitor = competitors.find(c => c.id === competitorId);
      posthog?.capture('competitor_selected', {
        competitor_id: competitorId,
        competitor_name: competitor?.competitor_brand_name,
        competitor_url: competitor?.primary_website,
        organization_id: organizationData?.id,
      });
    }
  };

  // Payment gate for competitor report generation
  const { PaymentGate, requirePayment } = usePaymentGate({ source: 'competitor_report' });

  // Fetch competitor reports to show in the table
  const { data: competitorReports = [] } = useReportsQuery(
    organizationData?.id,
    1,
    100,
    'COMPETITOR_REPORT'
  );

  // Helper to get the most recent report for a competitor
  const getCompetitorReport = (competitorId: string) => {
    const reports = competitorReports.filter(
      (r) => r.report_metadata?.competitor_id === competitorId
    );
    // Reports are returned sorted by created_at desc, so first is most recent
    return reports[0] || null;
  };



  // 2. Poll task tracking status by business_id
  // This polls /competitors/business/{businessId}/tasks every 3 seconds while there are in-progress tasks
  const { data: taskStatus } = useQuery({
    ...competitorTasksQueryOptions(business?.id ?? 0),
    enabled: !!business?.id,
  });

  // 2b. Poll report task tracking status for "Generating" state in Report column
  const { data: reportTaskStatus, refetch: refetchReportTasks } = useQuery({
    ...reportTasksQueryOptions(business?.id ?? 0),
    enabled: !!business?.id,
  });

  // Helper to check if a competitor has a report being generated (match by URL)
  const isReportGenerating = (competitor: Competitor) => {
    if (!reportTaskStatus?.has_in_progress) return false;
    return reportTaskStatus.tasks.some(
      (task) => task.state === 'IN_PROGRESS' && task.url === competitor.primary_website
    );
  };

  // Helper to check if a competitor has a FAILED report task (no successful report yet)
  const hasFailedReportTask = (competitor: Competitor) => {
    if (!reportTaskStatus?.tasks) return false;
    const report = getCompetitorReport(competitor.id);
    if (report) return false; // Has successful report, ignore old failures
    // Don't show failed state in UI - just show normal "Generate" button
    return false;
  };

  // Track previous has_in_progress to detect when all tasks complete
  const [prevHasInProgress, setPrevHasInProgress] = useState<boolean | null>(null);
  const [prevReportHasInProgress, setPrevReportHasInProgress] = useState<boolean | null>(null);

  // 3. When all competitor tasks complete (has_in_progress goes from true to false), refetch competitors
  useEffect(() => {
    if (taskStatus) {
      const currentHasInProgress = taskStatus.has_in_progress;

      // If we were polling and now there are no in-progress tasks, refetch competitors
      if (prevHasInProgress === true && currentHasInProgress === false) {
        // Invalidate notifications so any new notifications appear
        queryClient.invalidateQueries({ queryKey: ['notifications'] });
        queryClient.invalidateQueries({ queryKey: ['notificationCounts'] });
        if (business?.id) {
          queryClient.invalidateQueries({ queryKey: ['competitors', business.id] });
        }
      }

      setPrevHasInProgress(currentHasInProgress);
    }
  }, [taskStatus?.has_in_progress, business?.id, prevHasInProgress, queryClient]);

  // 3b. When all report tasks complete, refetch reports to update the Report column
  useEffect(() => {
    if (reportTaskStatus) {
      const currentHasInProgress = reportTaskStatus.has_in_progress;

      if (prevReportHasInProgress === true && currentHasInProgress === false) {
        // Invalidate reports query so "View" link appears
        // Use refetchType: 'active' and await it to ensure data is fetched before clearing generating state
        queryClient.invalidateQueries({ queryKey: ['reports'] }).then(() => {
          // Only reset local generating state AFTER reports have been refetched
          // This prevents the race condition where button shows "Generate" briefly
          setGeneratingReportIds(new Set());
        });
        // Invalidate notifications so any new notifications appear
        queryClient.invalidateQueries({ queryKey: ['notifications'] });
        queryClient.invalidateQueries({ queryKey: ['notificationCounts'] });
      }

      setPrevReportHasInProgress(currentHasInProgress);
    }
  }, [reportTaskStatus?.has_in_progress, prevReportHasInProgress, queryClient]);

  // Sort competitors: loading rows go to end, custom sort for campaign (nulls last)
  const competitorSortFns = useMemo(() => ({
    competitor_brand_name: (a: Competitor, b: Competitor, dir: 'asc' | 'desc') => {
      if (a.is_loading !== b.is_loading) return a.is_loading ? 1 : -1;
      const cmp = a.competitor_brand_name.localeCompare(b.competitor_brand_name);
      return dir === 'asc' ? cmp : -cmp;
    },
    competitor_closeness: (a: Competitor, b: Competitor, dir: 'asc' | 'desc') => {
      if (a.is_loading !== b.is_loading) return a.is_loading ? 1 : -1;
      const cmp = a.competitor_closeness - b.competitor_closeness;
      return dir === 'asc' ? cmp : -cmp;
    },
    campaign: (a: Competitor, b: Competitor, dir: 'asc' | 'desc') => {
      if (a.is_loading !== b.is_loading) return a.is_loading ? 1 : -1;
      const aName = a.campaign?.name || '';
      const bName = b.campaign?.name || '';
      if (!aName && !bName) return 0;
      if (!aName) return 1;
      if (!bName) return -1;
      const cmp = aName.localeCompare(bName);
      return dir === 'asc' ? cmp : -cmp;
    },
  }), []);

  const { sortedData: sortedCompetitors, handleSort: handleCompetitorSort, getSortDirection: getCompetitorSortDirection } = useTableSorting(
    competitors,
    { key: 'competitor_brand_name', direction: 'asc' },
    competitorSortFns
  );

  // The page loading state only on the true first load (business query still resolving, or competitors
  // first-loading with no cached data); its own delay keeps a fast cached nav from flashing a spinner. A
  // background refetch of cached data keeps the list on screen (never re-blanks) (#5342).
  if (isInitialLoading) {
    return <PageLoadingState />;
  }

  if (error) {
    return (
      <div className="flex h-64 items-center justify-center">
        <p className="text-[#B91C1C]">{error}</p>
      </div>
    );
  }

  // No business yet: nothing to list, inside the Business card like the rest.
  if (!business) {
    return (
      <EmptyState
        icon={<Swords className="h-12 w-12 text-gray-500" strokeWidth={1} />}
        title="No Competitors"
        description="Competitors will appear here once you create your first campaign"
      />
    );
  }

  // Check if we should show empty state (no competitors and no tasks in progress)
  const showEmptyState = competitors.length === 0 && !taskStatus?.has_in_progress;

  // The toolbar buttons go into the Business page's toolbar (#6672); the table
  // and the detail column fill its card, laid out as the Customers tab is.
  const actions = (
    <>
                {showEmptyState && business?.business_url && (
                  <Button
                    variant="outline"
                    className="h-9 text-gray-900"
                    disabled={generateMutation.isPending}
                    onClick={() => generateMutation.mutate()}
                  >
                    {generateMutation.isPending && <Spinner size="sm" className="mr-2" />}
                    Generate Competitors
                  </Button>
                )}
                {!showEmptyState && (
                  <>
                    {/* Explicit-save model (#5347/#4719): proximity edits stage locally; this CTA
                        appears only when dirty and persists them. No autosave. */}
                    {(isDirty || isSavingProximity) && (
                      <SaveChangesButton
                        className="h-9 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={!isDirty}
                        saving={isSavingProximity}
                        onClick={() => { void saveProximityChanges(); }}
                      />
                    )}
                    <Button variant="outline" className="h-9 text-gray-900" onClick={() => setIsAddModalOpen(true)}>
                      <Plus className="h-4 w-4 mr-2" />
                      Add
                    </Button>
                  </>
                )}
    </>
  );

  const emptyState = (
    <EmptyState
      icon={<Swords className="h-12 w-12 text-gray-500" strokeWidth={1} />}
      title="No Competitors Yet"
      description="Automatically detect competitors based on your business"
    />
  );

  // A competitor still being researched (#6715). It used to span the row; the
  // house table has no spanning cell, so the message sits in the Name column
  // and the rest of the row stays empty until the research lands.
  const researchingCell = (competitor: Competitor) => (
    <div className="flex items-center">
      <div className="flex-shrink-0 h-8 w-8">
        <div className="h-8 w-8 rounded-full bg-[#290BD9] bg-opacity-10 flex items-center justify-center">
          <Spinner size="sm" className="text-[#290BD9]" />
        </div>
      </div>
      <div className="ml-4">
        <div className="text-sm font-medium text-gray-900">
          Researching {competitor.primary_website?.replace(/^https?:\/\//, '') || 'competitor'}
        </div>
        <div className="text-xs text-gray-500">This can take 2-3 minutes.</div>
      </div>
    </div>
  );

  // The house table (#6715), with this page's own ordering kept: a loading row
  // sits last whichever way a column is sorted, and a competitor with no
  // campaign sorts after those that have one. The shared sorter expresses
  // neither, so the rows arrive sorted and the headings drive useTableSorting
  // as they did.
  const competitorColumns: ColumnDef<Competitor>[] = [
    {
      id: 'name',
      header: () => (
        <SortableHeader
          sortDirection={getCompetitorSortDirection('competitor_brand_name')}
          onSort={() => handleCompetitorSort('competitor_brand_name')}
        >
          Name
        </SortableHeader>
      ),
      enableSorting: false,
      meta: { className: 'px-6 w-[30%]' },
      cell: ({ row: { original: competitor } }) =>
        competitor.is_loading ? (
          researchingCell(competitor)
        ) : (
          <>
            <div className="flex items-center">
              <div className="flex-shrink-0 h-8 w-8">
                <div
                  className={`h-8 w-8 rounded-full flex items-center justify-center ${
                    competitor.site_resolved ? 'bg-[#290BD9] bg-opacity-10' : 'bg-gray-100'
                  }`}
                >
                  <span className={`text-xs font-medium ${competitor.site_resolved ? 'text-[#290BD9]' : 'text-gray-400'}`}>
                    {competitor.competitor_brand_name.substring(0, 2).toUpperCase()}
                  </span>
                </div>
              </div>
              <div className="ml-4 min-w-[200px]">
                <div className="flex items-center gap-1.5">
                  <div className="text-sm font-medium text-gray-900" title={competitor.competitor_brand_name}>
                    {competitor.competitor_brand_name.length > 36
                      ? competitor.competitor_brand_name.substring(0, 36) + '…'
                      : competitor.competitor_brand_name}
                  </div>
                  {/* The name of an unresolved row is only its domain, so the row has to say
                      why it looks so bare, and that it is not a competitor (#6754). */}
                  {!competitor.site_resolved && (
                    <span
                      title={competitor.description ?? UNRESOLVED_SITE_REASON}
                      className={`${PILL_METRICS} shrink-0 bg-gray-100 text-gray-500`}
                    >
                      Nothing here
                    </span>
                  )}
                </div>
                {competitor.primary_website && (
                  <a
                    href={competitor.primary_website}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="text-xs font-normal text-[#290BD9] hover:underline"
                    title={competitor.primary_website.replace(/^https?:\/\//, '')}
                  >
                    {(() => {
                      const domain = competitor.primary_website.replace(/^https?:\/\//, '');
                      return domain.length > 36 ? domain.substring(0, 36) + '…' : domain;
                    })()}
                  </a>
                )}
              </div>
            </div>
          </>
        ),
    },
    {
      id: 'proximity',
      header: () => (
        <SortableHeader
          sortDirection={getCompetitorSortDirection('competitor_closeness')}
          onSort={() => handleCompetitorSort('competitor_closeness')}
        >
          Proximity
        </SortableHeader>
      ),
      enableSorting: false,
      meta: { className: 'px-6' },
      cell: ({ row: { original: competitor } }) =>
        competitor.is_loading ? (
          null
        ) : (
          <>
            <ProximitySelect
              closeness={competitor.competitor_closeness}
              onChange={(score) => handleProximityChange(competitor, score)}
            />
          </>
        ),
    },
    {
      id: 'social',
      header: () => <>Social</>,
      enableSorting: false,
      meta: { className: 'px-6' },
      cell: ({ row: { original: competitor } }) =>
        competitor.is_loading ? (
          null
        ) : (
          <>
            <div className="flex gap-2">
              {competitor.social_media?.linkedin && (
                <a
                  href={competitor.social_media.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="h-6 w-6 rounded flex items-center justify-center bg-[#0A66C2] hover:bg-[#004182] transition-colors"
                >
                  <Linkedin className="h-3.5 w-3.5 text-white" />
                </a>
              )}
              {competitor.social_media?.twitter && (
                <a
                  href={competitor.social_media.twitter}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="h-6 w-6 rounded flex items-center justify-center bg-black hover:bg-gray-800 transition-colors"
                >
                  <img src="/x-logo.png" alt="X" className="h-3 w-3 invert" />
                </a>
              )}
              {competitor.social_media?.facebook && (
                <a
                  href={competitor.social_media.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="h-6 w-6 rounded flex items-center justify-center bg-[#1877F2] hover:bg-[#0E5FD4] transition-colors"
                >
                  <Facebook className="h-3.5 w-3.5 text-white" />
                </a>
              )}
              {competitor.social_media?.instagram && (
                <a
                  href={competitor.social_media.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="h-6 w-6 rounded flex items-center justify-center bg-[#E4405F] hover:bg-[#C13584] transition-colors"
                >
                  <Instagram className="h-3.5 w-3.5 text-white" />
                </a>
              )}
            </div>
          </>
        ),
    },
    {
      id: 'campaign',
      header: () => (
        <SortableHeader
          sortDirection={getCompetitorSortDirection('campaign')}
          onSort={() => handleCompetitorSort('campaign')}
        >
          Campaign
        </SortableHeader>
      ),
      enableSorting: false,
      // Campaign holds a control, not prose: one pill on one line. Name, which
      // wraps happily, and the bin, which is an icon, give it the room (#6743).
      meta: { className: 'px-6 whitespace-nowrap' },
      cell: ({ row: { original: competitor } }) =>
        competitor.is_loading ? (
          null
        ) : (
          <>
            {competitor.campaign ? (
              <div className="flex flex-col items-start gap-0.5">
                <CompetitorCampaignCell
                  campaign={competitor.campaign}
                  generating={isCampaignGenerating(competitor.campaign.id)}
                />
                {competitor.campaign.ad && (
                  <Link
                    to="/campaigns/google/$campaignslug"
                    params={{
                      campaignslug: competitor.campaign.slug,
                    }}
                    onClick={(e) => e.stopPropagation()}
                    className="text-xs text-gray-500 hover:text-[#290BD9] hover:underline"
                  >
                    View Ad
                  </Link>
                )}
              </div>
            ) : (
              <AddToCampaignSelect
                campaigns={competitorCampaigns}
                loading={isLoadingCompetitorCampaigns}
                busy={addingCompetitorId === competitor.id}
                unavailableReason={competitor.site_resolved ? undefined : UNRESOLVED_SITE_REASON}
                onPick={(campaignId) => addToCampaign(competitor, campaignId)}
              />
            )}
          </>
        ),
    },
    {
      id: 'report',
      header: () => <>Report</>,
      enableSorting: false,
      meta: { className: 'px-6' },
      cell: ({ row: { original: competitor } }) =>
        competitor.is_loading ? (
          null
        ) : (
          <>
            {(() => {
              const report = getCompetitorReport(competitor.id);
              const generating = isReportGenerating(competitor);

              if (report && !generating) {
                return (
                  <Link
                    to="/reports/$reportId"
                    params={{ reportId: String(report.id) }}
                    onClick={(e) => e.stopPropagation()}
                    className="text-[#290BD9] hover:underline flex items-center gap-1"
                  >
                    <Eye className="h-3.5 w-3.5" />
                    View
                  </Link>
                );
              }

              if (generating) {
                return (
                  <span className="text-gray-500 flex items-center gap-1">
                    <Spinner size="sm" />
                    Generating...
                  </span>
                );
              }

              const failed = hasFailedReportTask(competitor);

              return (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    requirePayment(async () => {
                      if (!organizationData?.id) return;
                      setGeneratingReportIds(prev => new Set(prev).add(competitor.id));

                      posthog?.capture('report_generation_started', {
                        report_type: 'COMPETITOR_REPORT',
                        competitor_id: competitor.id,
                        competitor_name: competitor.competitor_brand_name,
                        competitor_url: competitor.primary_website,
                        organization_id: organizationData.id,
                      });

                      try {
                        await generateCompetitorReport(competitor.id, {
                          organization_id: organizationData.id,
                          googleadsaccount_id: googleAdsAccount?.id ? parseInt(googleAdsAccount.id, 10) : undefined,
                          business_id: business?.id,
                        });
                        // Refetch report tasks so isReportGenerating() picks up the new task
                        await refetchReportTasks();
                      } catch (err) {
                        console.error('Error generating report:', err);
                        posthog?.capture('report_generation_failed', {
                          report_type: 'COMPETITOR_REPORT',
                          competitor_id: competitor.id,
                          competitor_name: competitor.competitor_brand_name,
                          error: err instanceof Error ? err.message : 'Unknown error',
                          organization_id: organizationData.id,
                        });
                        setGeneratingReportIds(prev => {
                          const next = new Set(prev);
                          next.delete(competitor.id);
                          return next;
                        });
                      }
                    });
                  }}
                  disabled={generatingReportIds.has(competitor.id)}
                  className={`hover:underline disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1 ${failed ? 'text-red-600' : 'text-[#290BD9]'}`}
                >
                  {generatingReportIds.has(competitor.id) ? (
                    <>
                      <Spinner size="sm" />
                      Generating...
                    </>
                  ) : failed ? (
                    <>
                      <AlertCircle className="h-3.5 w-3.5" />
                      Failed - Retry
                    </>
                  ) : (
                    <>
                      <FilePlus className="h-3.5 w-3.5" />
                      Generate
                    </>
                  )}
                </button>
              );
            })()}
          </>
        ),
    },
    {
      id: 'remove',
      header: () => null,
      enableSorting: false,
      meta: { className: 'px-4 w-14 text-center' },
      cell: ({ row: { original: competitor } }) =>
        competitor.is_loading ? (
          null
        ) : (
          <>
            <button
              onClick={(e) => { e.stopPropagation(); setCompetitorToDelete(competitor); }}
              className="p-1 rounded text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
              title="Remove competitor"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </>
        ),
    },
  ];

  const table =
    competitors.length === 0 && taskStatus?.has_in_progress ? (
      // Nothing found yet and the detector is still running.
      <div className="px-6 py-4">
        <div className="flex items-center">
          <div className="flex-shrink-0 h-8 w-8">
            <div className="h-8 w-8 rounded-full bg-[#290BD9] bg-opacity-10 flex items-center justify-center">
              <Spinner size="sm" className="text-[#290BD9]" />
            </div>
          </div>
          <div className="ml-4">
            <div className="text-sm font-medium text-gray-900">Finding competitors</div>
            <div className="text-xs text-gray-500">This can take 2-3 minutes.</div>
          </div>
        </div>
      </div>
    ) : (
      <DataTable<Competitor>
        data={sortedCompetitors}
        columns={competitorColumns}
        variant="flush"
        pageSize={999}
        getRowId={(competitor) => competitor.id}
        onRowClick={(competitor) => selectCompetitor(competitor.id)}
        getRowClassName={(competitor) => (selectedCompetitorId === competitor.id ? 'bg-[#290BD9]/10' : '')}
        emptyState="No competitors match the filter."
      />
    );

  const details = selectedCompetitorId ? (
    (() => {
                const competitor = competitors.find(c => c.id === selectedCompetitorId);
                if (!competitor) return null;

                return (
                  <div className="space-y-3">
                    <h3 className="text-lg font-semibold text-gray-900 break-words">{competitor.competitor_brand_name}</h3>

                    <div className="space-y-3">
                      {competitor.competitor_metadata?.industry && (
                        <div>
                          <div className="text-xs text-gray-700 mb-1">Industry Focus</div>
                          <div className="text-gray-900">{competitor.competitor_metadata.industry}</div>
                        </div>
                      )}

                      {competitor.competitor_metadata?.audience && (
                        <div>
                          <div className="text-xs text-gray-700 mb-1">Target Audience</div>
                          <div className="text-gray-900">{competitor.competitor_metadata.audience}</div>
                        </div>
                      )}

                      {competitor.description && (
                        <div>
                          <div className="text-xs text-gray-700 mb-1">Description</div>
                          <div className="text-gray-900">{competitor.description}</div>
                        </div>
                      )}

                      {/* Competitor Proximity */}
                      <div>
                        <div className="text-xs text-gray-700 mb-1">Competitor Proximity</div>
                        <ProximitySelect
                          closeness={competitor.competitor_closeness}
                          onChange={(score) => handleProximityChange(competitor, score)}
                        />
                      </div>

                      {/* Ad Libraries */}
                      {(competitor.primary_website || competitor.competitor_brand_name) && (
                        <div>
                          <div className="text-xs text-gray-700 mb-1">Ad Libraries</div>
                          <div className="flex gap-2">
                            {competitor.primary_website && (
                              <a
                                href={`https://adstransparency.google.com/?region=anywhere&domain=${encodeURIComponent(competitor.primary_website.replace(/^https?:\/\//, '').replace(/\/.*$/, ''))}&format=TEXT&preset-date=Last+30+days`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="h-6 w-6 flex-shrink-0"
                                title="View Google Ads"
                              >
                                <img src="/google-ads.svg" className="h-6 w-6" alt="Google Ads" />
                              </a>
                            )}
                            {competitor.competitor_brand_name && (
                              <a
                                href={`https://www.facebook.com/ads/library/?active_status=active&ad_type=all&country=ALL&q=${encodeURIComponent(competitor.competitor_brand_name)}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="h-6 w-6 flex-shrink-0 rounded flex items-center justify-center bg-[#1877F2] hover:bg-[#0E5FD4] transition-colors"
                                title="View Meta Ads"
                              >
                                <Facebook className="h-3.5 w-3.5 text-white" />
                              </a>
                            )}
                          </div>
                        </div>
                      )}

                      {competitor.location && (competitor.location.town || competitor.location.region || competitor.location.country) && (
                        <div>
                          <div className="text-xs text-gray-700 mb-1">Location</div>
                          <div className="text-gray-900">
                            {[competitor.location.town, competitor.location.region, competitor.location.country]
                              .filter(Boolean)
                              .join(', ')}
                          </div>
                        </div>
                      )}

                      {competitor.competitor_metadata?.founding_year && (
                        <div>
                          <div className="text-xs text-gray-700 mb-1">Founded</div>
                          <div className="text-gray-900">{competitor.competitor_metadata.founding_year}</div>
                        </div>
                      )}

                      {competitor.competitor_metadata?.founders && competitor.competitor_metadata.founders.length > 0 && (
                        <div>
                          <div className="text-xs text-gray-700 mb-1">Founders</div>
                          <div className="flex flex-wrap gap-1.5 mt-1">
                            {competitor.competitor_metadata.founders.map((founder, idx) => (
                              <span key={idx} className="inline-flex items-center px-2 py-0.5 rounded-md text-xs bg-gray-100 text-gray-900">
                                {founder}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
    })()
  ) : (
    <EmptyState
      className="border-0 shadow-none"
      compact
      icon={<Users className="h-12 w-12 text-gray-500" strokeWidth={1} />}
      title="Select a Competitor"
      description="Click any row in the table to view detailed competitor analysis"
    />
  );

  const modals = (
    <>
      <AddCompetitorModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        businessId={business.id}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['competitors', business.id] });
        }}
      />

      {/* Payment Gate Modal */}
      <PaymentGate />

      {/* Remove Competitor Confirmation Modal */}
      <ConformationModal
        isOpen={competitorToDelete !== null}
        onClose={() => setCompetitorToDelete(null)}
        onConfirm={() => {
          if (competitorToDelete) {
            deleteMutation.mutate(competitorToDelete.id);
          }
        }}
        title="Remove Competitor"
        description={
          competitorToDelete?.campaign
            ? `Are you sure you want to remove ${competitorToDelete.competitor_brand_name}? This competitor will be removed from all campaigns.`
            : `Are you sure you want to remove ${competitorToDelete?.competitor_brand_name ?? ''}?`
        }
        confirmText="Remove"
        isLoading={deleteMutation.isPending}
      />

      {/* #4792: warn before navigating away with unsaved proximity edits */}
      {UnsavedChangesModal}
    </>
  );

  return (
    <>
      {actionsContainer && createPortal(actions, actionsContainer)}
      {showEmptyState ? (
        emptyState
      ) : (
        <div className="grid grid-cols-12 gap-0 -m-6">
          {/* The flush table pulls its own -mx-6; this column cancels the
              panel padding already, so give it back here or the table hangs
              off the card's edge (#6715). Landing Pages does the same. */}
          <div className="col-span-9 border-r border-gray-200">
            <div className="px-6 pb-6">{table}</div>
          </div>
          <div className="relative col-span-3 p-6">
            {/* The hint that nothing is chosen centres itself in the panel
                (#6743). It is bounded by the viewport as well as the panel:
                centred in a 150-row table it would sit thousands of pixels
                down, which is the failure #6724 warned about, inverted. */}
            <div className={selectedCompetitorId ? 'sticky top-6' : 'absolute inset-6 flex items-start justify-center'}>{details}</div>
          </div>
        </div>
      )}
      {modals}
    </>
  );
}
